import { Box, Typography } from "@mui/material";
import * as chartUtils from "chartUtils";
import * as logger from "common/logger";
import * as numbers from "common/numbers";
import * as labels from "config.labels";
import { connect } from "react-redux";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as peersSelectors from "store/selectors/peers/peersSelectors";
import * as towerSelectors from "store/selectors/pricing/tower/towerPricingSelectors";

const COLORS = [
  "#FF634E",
  "#FFAB53",
  "#4CAF50",
  "#2196F3",
  "#9C27B0",
  "#FF5722",
  "#607D8B",
  "#795548",
  "#E91E63",
  "#00BCD4",
];

export const parseData = (peerData, currentTower) => {
  const layersData = [];
  const validIndices = new Set();

  if (currentTower && currentTower.length) {
    currentTower.forEach((layer) => {
      if (!layer) return;
      const attachment =
        numbers.parseFloatOrNull(layer.effectiveAttachment) ??
        numbers.parseFloatOrNull(layer.attachment) ??
        0;
      const limit = numbers.parseFloatOrNull(layer.limit) || 0;
      const midpoint = attachment + limit / 2;
      layersData.push({
        midpoint,
        rate_current: numbers.parseFloatOrNull(layer.grossRPM),
        peerName: "Current Submission",
      });
    });
  }

  if (peerData) {
    Object.entries(peerData).forEach(([filename, data], index) => {
      if (!data) return;

      try {
        const tower = towerSelectors.getTower.resultFunc(
          data?.pricing?.tower?.layers || [],
          data?.input?.program,
          data?.input?.claims?.selfInsuredRetentions || []
        );

        if (!tower || !tower.length) return;

        tower.forEach((layer) => {
          if (!layer) return;
          const attachment =
            numbers.parseFloatOrNull(layer.effectiveAttachment) ??
            numbers.parseFloatOrNull(layer.attachment) ??
            0;
          const limit = numbers.parseFloatOrNull(layer.limit) || 0;
          const midpoint = attachment + limit / 2;
          layersData.push({
            midpoint,
            [`rate_${index}`]: numbers.parseFloatOrNull(layer.grossRPM),
            peerName: data.input?.program?.insured || filename,
          });
          validIndices.add(index);
        });
      } catch (error) {
        logger.error("Peer Tower Layer Chart Error:", error);
      }
    });
  }

  const midpoints = new Set(layersData.map((item) => item.midpoint));

  const mergedData = Array.from(midpoints).map((midpoint) => {
    const dataPoint = { midpoint };

    const currentLayer = layersData.find(
      (item) => item.midpoint === midpoint && item.rate_current !== undefined
    );
    if (currentLayer) {
      dataPoint.rate_current = currentLayer.rate_current;
      dataPoint.peer_current = currentLayer.peerName;
    }

    if (peerData) {
      Object.keys(peerData).forEach((_, index) => {
        const peerLayer = layersData.find(
          (item) =>
            item.midpoint === midpoint && item[`rate_${index}`] !== undefined
        );
        if (peerLayer) {
          dataPoint[`rate_${index}`] = peerLayer[`rate_${index}`];
          dataPoint[`peer_${index}`] = peerLayer.peerName;
        }
      });
    }

    return dataPoint;
  });

  return {
    data: mergedData.sort((a, b) => a.midpoint - b.midpoint),
    validIndices,
  };
};

const PeerTowerLayerChart = (props) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const { peerData, currentTower } = props;
  const Y_LABEL = translateLabel(
    labels.PEER_COMPARISON_TOWER_RATE_CHART_Y_LABEL,
    {
      default: "Rate Per Million",
    }
  );

  const X_LABEL = translateLabel(
    labels.PEER_COMPARISON_TOWER_RATE_CHART_X_LABEL,
    {
      default: "Layer Midpoint",
    }
  );

  const chart = parseData(peerData, currentTower);

  const {
    chartRef,
  } = chartUtils.useTooltipPositionerFixingBottomRightToChartTopRight();

  const hasValidData =
    chart && chart.data && Array.isArray(chart.data) && chart.data.length > 0;

  if (!hasValidData) {
    return (
      <Box
        display={"flex"}
        justifyContent={"center"}
        alignItems={"center"}
        height={"100%"}
        padding={2}
      >
        <Typography variant={"h6"} color={"textSecondary"}>
          {translateLabel(labels.TOWER_RATE_CHART_NO_DATA, {
            default: "No Peer Tower Data Available, or GWP missing",
          })}
        </Typography>
      </Box>
    );
  }

  const chartMargins = {
    top: 20,
    right: 30,
    left: 20,
    bottom: 5,
  };

  const defaultStyles = {
    position: "relative",
    width: "100%",
    paddingBottom: "500px",
  };

  return (
    <div style={defaultStyles}>
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          top: 0,
        }}
      >
        <ResponsiveContainer width={"100%"} height={"100%"}>
          <ComposedChart data={chart.data} margin={chartMargins} ref={chartRef}>
            <CartesianGrid strokeDasharray={"3 3"} />

            <XAxis
              type={"number"}
              dataKey={"midpoint"}
              name={"Layer Midpoint"}
              domain={[0, "dataMax"]}
              tickFormatter={(value) => numbers.abbreviated(value)}
              label={{
                value: X_LABEL,
                position: "insideBottom",
                offset: -5,
              }}
            />

            <YAxis
              domain={[0, (dataMax) => numbers.roundUpNice(dataMax)]}
              tickFormatter={(value) => numbers.abbreviated(value)}
              label={{
                value: Y_LABEL,
                angle: -90,
                position: "insideLeft",
              }}
            />

            <Tooltip
              isAnimationActive={false}
              formatter={(value, name) => {
                if (name === "rate_current") {
                  return [numbers.abbreviated(value), "Current Submission"];
                }
                const peerIndex = name?.split?.("_")?.[1];
                if (!peerIndex || !chart?.data || !Array.isArray(chart.data)) {
                  return [numbers.abbreviated(value), "Unknown"];
                }
                const peerName =
                  chart.data.find((item) => item?.[`peer_${peerIndex}`])?.[
                    `peer_${peerIndex}`
                  ] || "Unknown";
                return [numbers.abbreviated(value), peerName];
              }}
              labelFormatter={(label) =>
                `Midpoint: ${numbers.abbreviated(label ?? 0)}`
              }
            />

            <Legend
              verticalAlign={"top"}
              height={36}
              formatter={(value) => {
                if (value === "rate_current") {
                  return "Current Submission";
                }
                const peerIndex = value?.split?.("_")?.[1];
                if (!peerIndex || !chart?.data || !Array.isArray(chart.data)) {
                  return value || "Unknown";
                }
                const peerName = chart.data.find(
                  (item) => item?.[`peer_${peerIndex}`]
                )?.[`peer_${peerIndex}`];
                return peerName || value;
              }}
            />

            {currentTower && currentTower.length > 0 && (
              <Line
                key={"current"}
                type={"monotone"}
                connectNulls={true}
                dataKey={"rate_current"}
                stroke={"#000000"}
                name={"rate_current"}
                strokeWidth={3}
                dot={{ r: 4 }}
              />
            )}

            {chart.validIndices &&
              Array.from(chart.validIndices).map((index) => (
                <Line
                  key={index}
                  type={"monotone"}
                  connectNulls={true}
                  dataKey={`rate_${index}`}
                  stroke={COLORS[index % COLORS.length]}
                  name={`rate_${index}`}
                  strokeWidth={3}
                  dot={{ r: 4 }}
                />
              ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  peerData: peersSelectors.rawPeerData(state),
  currentTower: towerSelectors.getTower(state),
});

export default connect(mapStateToProps)(PeerTowerLayerChart);
